/**
 * DragManager - Handles element dragging with alignment guides
 * Implements smart snapping to edges and centers of other elements
 */

import Konva from 'konva';
import { EventEmitter } from '../core/EventEmitter';
import { Point, BoundingBox } from '../core/types';
import { EditableElement, AlignmentGuide } from '../editing/types';

export interface DragManagerOptions {
  snapThreshold?: number;
  enableSnapping?: boolean;
  showGuides?: boolean;
}

const DEFAULT_OPTIONS: Required<DragManagerOptions> = {
  snapThreshold: 5,
  enableSnapping: true,
  showGuides: true,
};

export class DragManager {
  private layer: Konva.Layer;
  private emitter: EventEmitter;
  private options: Required<DragManagerOptions>;
  
  // Elements map for alignment calculations
  private elements: Map<string, EditableElement> = new Map();
  
  // Active guides
  private activeGuides: AlignmentGuide[] = [];
  private guideLines: Konva.Line[] = [];
  
  // Drag state
  private isDragging: boolean = false;
  private dragElementIds: string[] = [];

  constructor(
    layer: Konva.Layer,
    emitter: EventEmitter,
    options: DragManagerOptions = {}
  ) {
    this.layer = layer;
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Register elements for alignment calculations
   */
  setElements(elements: EditableElement[]): void {
    this.elements.clear();
    elements.forEach((el) => {
      this.elements.set(el.id, el);
    });
  }

  /**
   * Update a single element
   */
  updateElement(element: EditableElement): void {
    this.elements.set(element.id, element);
  }

  /**
   * Remove an element
   */
  removeElement(id: string): void {
    this.elements.delete(id);
  }

  /**
   * Start dragging elements
   */
  startDrag(elementIds: string[]): void {
    this.isDragging = true;
    this.dragElementIds = [...elementIds];
  }

  /**
   * Handle drag move - check for alignment and snap
   */
  onDragMove(movingElement: EditableElement): { x?: number; y?: number } {
    if (!this.options.enableSnapping) {
      return {};
    }
    
    const guides: AlignmentGuide[] = [];
    const movingPoints = this.getAlignmentPoints(movingElement);
    let snappedX: number | undefined;
    let snappedY: number | undefined;
    
    // Check against other elements
    this.elements.forEach((el, id) => {
      // Skip if element is being moved
      if (this.dragElementIds.includes(id)) return;
      
      const points = this.getAlignmentPoints(el);
      
      // Check vertical alignment (X axis)
      for (const movingKey of ['left', 'centerX', 'right'] as const) {
        const movingValue = movingPoints[movingKey];
        
        for (const targetKey of ['left', 'centerX', 'right'] as const) {
          const targetValue = points[targetKey];
          
          if (Math.abs(movingValue - targetValue) < this.options.snapThreshold) {
            guides.push({
              orientation: 'vertical',
              position: targetValue,
              matchedElements: [movingElement.id, id],
            });
            
            if (snappedX === undefined) {
              // Calculate offset to snap
              const offset = targetValue - movingValue;
              snappedX = movingElement.x + offset;
            }
          }
        }
      }
      
      // Check horizontal alignment (Y axis)
      for (const movingKey of ['top', 'centerY', 'bottom'] as const) {
        const movingValue = movingPoints[movingKey];
        
        for (const targetKey of ['top', 'centerY', 'bottom'] as const) {
          const targetValue = points[targetKey];
          
          if (Math.abs(movingValue - targetValue) < this.options.snapThreshold) {
            guides.push({
              orientation: 'horizontal',
              position: targetValue,
              matchedElements: [movingElement.id, id],
            });
            
            if (snappedY === undefined) {
              // Calculate offset to snap
              const offset = targetValue - movingValue;
              snappedY = movingElement.y + offset;
            }
          }
        }
      }
    });
    
    // Update active guides
    this.activeGuides = guides;
    
    if (this.options.showGuides) {
      this.updateGuideLines();
    }
    
    return {
      x: snappedX,
      y: snappedY,
    };
  }

  /**
   * End drag - clear guides
   */
  endDrag(): void {
    this.isDragging = false;
    this.dragElementIds = [];
    this.clearGuides();
  }

  /**
   * Get alignment points for an element
   */
  private getAlignmentPoints(element: EditableElement): {
    left: number;
    centerX: number;
    right: number;
    top: number;
    centerY: number;
    bottom: number;
  } {
    return {
      left: element.x,
      centerX: element.x + element.width / 2,
      right: element.x + element.width,
      top: element.y,
      centerY: element.y + element.height / 2,
      bottom: element.y + element.height,
    };
  }

  /**
   * Update visual guide lines
   */
  private updateGuideLines(): void {
    // Remove existing guide lines
    this.clearGuideLines();
    
    // Create new guide lines
    this.activeGuides.forEach((guide) => {
      const line = new Konva.Line({
        points: this.calculateGuidePoints(guide),
        stroke: '#FF00FF',
        strokeWidth: 1,
        dash: [5, 5],
        listening: false,
        perfectDrawEnabled: false,
      });
      
      this.layer.add(line);
      line.moveToTop();
      this.guideLines.push(line);
    });
    
    this.layer.batchDraw();
  }

  /**
   * Calculate guide line points based on canvas size
   */
  private calculateGuidePoints(guide: AlignmentGuide): number[] {
    const stage = this.layer.getStage();
    if (!stage) return [];
    
    const width = stage.width() * 10; // Extend beyond visible area
    const height = stage.height() * 10;
    
    if (guide.orientation === 'vertical') {
      return [guide.position, -height, guide.position, height];
    } else {
      return [-width, guide.position, width, guide.position];
    }
  }

  /**
   * Clear visual guide lines
   */
  private clearGuideLines(): void {
    this.guideLines.forEach((line) => line.destroy());
    this.guideLines = [];
  }

  /**
   * Clear all guides (lines and state)
   */
  clearGuides(): void {
    this.clearGuideLines();
    this.activeGuides = [];
    this.layer.batchDraw();
  }

  /**
   * Get active guides
   */
  getActiveGuides(): AlignmentGuide[] {
    return [...this.activeGuides];
  }

  /**
   * Check if currently dragging
   */
  getIsDragging(): boolean {
    return this.isDragging;
  }

  /**
   * Set snap threshold
   */
  setSnapThreshold(threshold: number): void {
    this.options.snapThreshold = threshold;
  }

  /**
   * Enable or disable snapping
   */
  setSnappingEnabled(enabled: boolean): void {
    this.options.enableSnapping = enabled;
    if (!enabled) {
      this.clearGuides();
    }
  }

  /**
   * Enable or disable guide visualization
   */
  setGuidesVisible(visible: boolean): void {
    this.options.showGuides = visible;
    if (!visible) {
      this.clearGuideLines();
    }
  }

  /**
   * Snap a position to the grid
   */
  snapToGrid(x: number, y: number, gridSize: number = 10): Point {
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize,
    };
  }

  /**
   * Destroy the drag manager
   */
  destroy(): void {
    this.clearGuideLines();
    this.elements.clear();
    this.activeGuides = [];
    this.dragElementIds = [];
  }
}
